import './App.css'

import { StyledEngineProvider } from '@mui/styled-engine-sc'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import { appAction, uiStateAction } from './actions/app.action'
import { setAuthTokenFromLocalStorage } from './actions/auth.action'
import { countriesCall, masterDataCall } from './actions/masterData.action'
import { fetchTenantMetadata } from './actions/metadata.action'
import { getAllDistricts, getAllSchools } from './actions/organization.action'
import { setAxiosBase } from './api/httpClient'
import localStorageService from './api/localStorageService'
import defaultTheme from './assets/light/theme'
import ClientWrapper from './components/ClientWrapper'
import { THEME } from './helpers/constants'
import { selectApp, selectAuth, selectUser } from './helpers/selectors'
import { get, isEmpty } from './helpers/utils'
import { CssBaseline, ThemeProvider } from './MaterialImports'
import Routes from './Routes'

function App({
  appAction,
  setAuthTokenFromLocalStorage,
  masterDataCall,
  countriesCall,
  fetchTenantMetadata,
  auth,
  getAllDistricts,
  getAllSchools,
  app,
  authUser,
  uiStateAction,
}) {
  const { i18n } = useTranslation()
  const [theme, setTheme] = useState(THEME.LIGHT)
  const [currentTheme, setCurrentTheme] = useState(defaultTheme)

  useEffect(() => {
    if (auth.isLoggedIn) {
      const userTheme = get(
        authUser,
        'user_principal.attributes.user_theme[0]',
        localStorageService.getTheme() || THEME.LIGHT
      )
      setTheme(userTheme)
    } else {
      setTheme(THEME.LIGHT)
    }
  }, [authUser, auth])

  useEffect(() => {
    import(`./assets/${theme}/theme`)
      .then((themes) => {
        setCurrentTheme(themes.default)
      })
      .catch((error) => {
        console.error('Error loading theme:', error)
        setCurrentTheme(defaultTheme)
      })
  }, [theme])

  window.setLanguage = (lang) => i18n.changeLanguage(lang)

  useEffect(() => {
    appAction({
      baseURL: __SERVICE_URL_SECURED__,
      redirectBase: __REDIRECT_BASE_SECURED__,
      identityBase: __LOGIN_URL_SECURED__,
      userURL: __USER_URL__,
      ckAccessKey: __CKEDITOR_ACCESS_KEY__,
      ckConverterURL: __CKEDITOR_CONVERTER_URL__,
      showLmsMenu: __SHOW_LMS_MENU__,
      enableLicense: __ENABLE_LICENSE__,
    })
    setAxiosBase({ baseURL: __SERVICE_URL_SECURED__ })
  }, [])

  useEffect(() => {
    if (app.appReady) {
      setAuthTokenFromLocalStorage() // Do not change calling index
    }
  }, [app.appReady])

  useEffect(() => {
    if (auth.isLoggedIn) {
      masterDataCall()
      countriesCall({
        q: '',
        sort_by: 'name',
        sort_order: 'asc',
        per_page: 500,
        page: 1,
      })
      fetchTenantMetadata()
      getAllDistricts()
      getAllSchools()
    }
  }, [auth.isLoggedIn])

  useEffect(() => {
    const localstorageUIState = localStorageService.getUIState()
    if (!isEmpty(localstorageUIState)) {
      uiStateAction(localstorageUIState)
      localStorageService.setUIState({})
    }
  }, [])

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={currentTheme}>
        <CssBaseline />
        <div className="app">
          <ClientWrapper>
            <Routes />
          </ClientWrapper>
        </div>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

App.propTypes = {
  appAction: PropTypes.func,
  setAuthTokenFromLocalStorage: PropTypes.func,
  masterDataCall: PropTypes.func,
  countriesCall: PropTypes.func,
  fetchTenantMetadata: PropTypes.func,
  auth: PropTypes.object,
  getAllDistricts: PropTypes.func,
  getAllSchools: PropTypes.func,
  app: PropTypes.object,
  authUser: PropTypes.object,
  uiStateAction: PropTypes.func,
}

App.defaultProps = {
  appAction: () => {},
  setAuthTokenFromLocalStorage: () => {},
  masterDataCall: () => {},
  countriesCall: () => {},
  fetchTenantMetadata: () => {},
  auth: {},
  getAllDistricts: () => {},
  getAllSchools: () => {},
  app: {},
  authUser: {},
  uiStateAction: () => {},
}

const mapStateToProps = (state) => ({
  auth: selectAuth(state),
  app: selectApp(state),
  authUser: selectUser(state),
})

export default connect(mapStateToProps, {
  appAction,
  setAuthTokenFromLocalStorage,
  masterDataCall,
  countriesCall,
  fetchTenantMetadata,
  getAllDistricts,
  getAllSchools,
  uiStateAction,
})(App)
